import { useLayoutEffect, useRef } from "react"
import { Outlet, useLocation } from "react-router-dom"

import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { AiHealthBanner } from "@/components/layout/ai-health-banner"
import { AppHeader } from "@/components/layout/app-header"
import { AppSidebar } from "@/components/layout/app-sidebar"
import { CommandMenuProvider } from "@/components/layout/command-menu"
import { MobileNav } from "@/components/layout/mobile-nav"
import { RouteProgress } from "@/components/layout/route-progress"

/** Expanded by default on wide screens (unless the user collapsed it); an icon rail on tablets. */
function readSidebarPreference() {
  if (window.innerWidth < 1024) return false
  return !document.cookie.split("; ").includes("sidebar_state=false")
}

/**
 * Authenticated application frame.
 * Desktop: sidebar + inset panel. Phone: header, scrolling page, bottom tab bar.
 * The page scrolls inside the panel so the header, the AI/connection warning under it and the tab bar never move.
 *
 * The panel clips (`overflow-clip`) instead of hiding overflow: a hidden-overflow box is still a
 * scroll container, so focus() or scrollIntoView() could scroll it and push the header out of
 * view with no way back. `#main-scroll` is `relative` so absolutely positioned descendants
 * (screen-reader labels, hidden form inputs) scroll with the page instead of leaking out of it.
 */
export function AppShell() {
  const { pathname } = useLocation()
  const scrollRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [pathname])

  return (
    <SidebarProvider defaultOpen={readSidebarPreference()}>
      <CommandMenuProvider>
        <AppSidebar />
        <SidebarInset className="relative h-dvh min-w-0 overflow-clip md:h-[calc(100dvh-1rem)] md:border md:shadow-none">
          <RouteProgress />
          <AppHeader />
          <AiHealthBanner />
          <div ref={scrollRef} id="main-scroll" className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
            <div className="mx-auto w-full max-w-7xl px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
              <Outlet />
            </div>
          </div>
          <MobileNav />
        </SidebarInset>
      </CommandMenuProvider>
    </SidebarProvider>
  )
}
